import "./CreatePolicy.css";

import { Breadcrumb, Button, Form, Input, message, Select } from "antd";
import { useEffect, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { CgScreen } from "react-icons/cg";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Editor } from "../../../_lib/_components";
import {
  type CategoryType,
  createPolicy,
  deletePolicy,
  getCategories,
  getPackages,
  getSubcategories,
  type PolicyFormType,
  type PolicyType,
  type SubCategoryType,
  updatePolicy,
  useAppSelector,
} from "../../../_lib/_store";

interface CreatePolicyPageProps {
  type: "create" | "edit";
}

export const CreatePolicyPage = ({ type }: CreatePolicyPageProps) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { isLoading } = useAppSelector((state) => state.policy);
  const { isLoading: packageLoading, packages } = useAppSelector(
    (state) => state.package,
  );
  const { isLoading: categoryLoading, categories } = useAppSelector(
    (state) => state.category,
  );
  const { isLoading: subcategoryLoading, subCategories } = useAppSelector(
    (state) => state.subCategory,
  );

  const [filteredCategories, setFilteredCategories] = useState<CategoryType[]>(
    [],
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    SubCategoryType[]
  >([]);
  const navigate = useNavigate();
  const { state } = useLocation();

  const isEdit = type === "edit";

  useEffect(() => {
    const policyObj = state?.policy;
    if (isEdit && !policyObj) {
      navigate("/policy");
    }
    if (isEdit) {
      form.setFieldsValue(policyObj);
      if (policyObj?.packageId) {
        setFilteredCategories(
          categories.filter((c) => c.packageId === policyObj?.packageId),
        );
      }
      if (policyObj?.categoryId) {
        setFilteredSubCategories(
          subCategories.filter((s) => s.categoryId === policyObj.categoryId),
        );
      }
    }
  }, [state, packages, categories, subCategories]);

  useEffect(() => {
    getData();
  }, [state]);

  const getData = async () => {
    await getPackages(messageApi);
    await getCategories(messageApi);
    await getSubcategories(messageApi);
  };

  const handleValuesChange = (changedValues: PolicyType) => {
    if (changedValues.packageId) {
      setFilteredCategories(
        categories.filter((c) => c.packageId === changedValues.packageId),
      );
      form.setFieldsValue({
        categoryId: undefined,
        subCategoryId: undefined,
      });
      setFilteredSubCategories([]);
    }

    if (changedValues.categoryId) {
      setFilteredSubCategories(
        subCategories.filter((s) => s.categoryId === changedValues.categoryId),
      );
      form.setFieldsValue({ subCategoryId: undefined });
    }
    if (changedValues.subCategoryId) {
      setFilteredSubCategories(subCategories);
    }
  };

  const handleSubmit = async (values: PolicyFormType) => {
    const payload = {
      ...values,
      ...(isEdit ? { id: state.policy.id } : {}),
    };

    if (isEdit) {
      const res = await updatePolicy(messageApi, payload);
      if (res) {
        navigate("/policy/edit", {
          state: { policy: res },
          replace: true,
        });
      }
    } else {
      const res = await createPolicy(messageApi, payload);
      if (res) {
        form.resetFields();
      }
    }
  };

  const handleDelete = async () => {
    const res = await deletePolicy(messageApi, state.policy.id);
    if (res) {
      navigate("/policy");
    }
  };

  return (
    <>
      {contextHolder}
      <div className="createPackage">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/policy">Policy</Link>,
            },
            {
              title: isEdit ? "Edit" : "Create new",
            },
          ]}
        />

        {!isEdit ? (
          <h2 className="title">Create new</h2>
        ) : (
          <div className="titleHeader">
            <h2 className="title">Edit</h2>
            <div className="actionButton">
              <Button
                icon={<CgScreen size={17} />}
                size="large"
                onClick={() =>
                  navigate(`/policy/show`, {
                    state: { policy: state?.policy },
                  })
                }
              >
                Show
              </Button>
              <Button
                icon={<BsTrash />}
                size="large"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="form">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Policy title is required" }]}
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Package"
              name="packageId"
              rules={[{ required: true, message: "Package is Required" }]}
              help={
                form.getFieldValue("packageId") ? "" : "This field is required."
              }
            >
              <Select
                loading={packageLoading}
                size="large"
                options={packages?.map((v) => ({
                  label: v?.name,
                  value: v?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Category"
              name="categoryId"
              rules={[{ required: true, message: "Category is Required" }]}
              help={
                form.getFieldValue("categoryId")
                  ? ""
                  : "Please select a package first to enable category selection and this field is required."
              }
            >
              <Select
                loading={categoryLoading}
                size="large"
                options={filteredCategories.map((c) => ({
                  label: c?.name,
                  value: c?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Sub Category"
              name="subCategoryId"
              help={
                form.getFieldValue("subCategoryId")
                  ? ""
                  : "Please select a category first to enable subcategory selection."
              }
            >
              <Select
                loading={subcategoryLoading}
                size="large"
                options={filteredSubCategories.map((s) => ({
                  label: s?.name,
                  value: s?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Content"
              name="content"
              valuePropName="value"
              getValueFromEvent={(val: string) => val}
              rules={[{ required: true, message: "Content is required" }]}
            >
              <Editor
                value=""
                onChange={() => console.log("")}
                placeholder="Enter content"
                height={400}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: "150px" }}
                loading={isLoading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};
